<template>
  <!--
    ✅ SSR + Hydration-safe Footer (Nuxt 4 / Vue 3 / Tailwind 4)
    - Packages werden SSR geladen (useAsyncData) -> kein onMounted() Fetch, kein Hydration-Mismatch
    - Language Switch lädt KEINE Packages neu -> useAsyncData Key ist konstant + API liefert alle slugs/titles
    - Links/Labels wechseln rein über computed (locale) -> ultra schnell
  -->
  <footer class="app-footer" aria-label="Footer">
    <!-- Column 1: Payment methods -->
    <div class="centered-flex">
      <!--
        ❗ Kein v-lazy-load Directive:
        Directives verursachen oft SSR/Client DOM Unterschiede => Hydration-Warnungen.
        Native loading="lazy" + decoding/fetchpriority ist robust & performant.
      -->
      <img
        v-once
        src="/images/paymethods.png"
        class="payment-methods-icon"
        alt="Payment Methods"
        loading="lazy"
        decoding="async"
        fetchpriority="low"
        width="384"
        height="194"
      />
    </div>

    <!-- Column 2: Packages -->
    <div class="centered-flex">
      <span class="footer-heading">{{ t('footer.packages') }}</span>
      <div class="footer-heading-line" />

      <ul class="idf-footer-sites" aria-label="Packages">
        <!-- SSR: Pending-Text anstelle von leeren Listen -->
        <li v-if="packagesPending" class="opacity-70 text-sm py-2">
          {{ t('common.loading') }}
        </li>

        <li v-for="item in packageLinks" v-else :key="item.key">
          <NuxtLink :to="item.to" class="hover:text-shade2">
            <span class="text-sm">{{ item.label }}</span>
          </NuxtLink>
        </li>
      </ul>
    </div>

    <!-- Column 3: idmanify.com Links -->
    <div class="centered-flex">
      <span class="footer-heading">{{ t('footer.idmanifyCom') }}</span>
      <div class="footer-heading-line" />

      <ul class="idf-footer-sites" aria-label="Idmanify Links">
        <li>
          <button
            type="button"
            data-cy="privacy-settings"
            class="text-sm hover:text-shade2"
            @click="privacy.open"
          >
            {{ t('cookieConsent.settings') }}
          </button>
        </li>
        <li v-for="item in siteLinks" :key="item.key">
          <NuxtLink :to="item.to" class="hover:text-shade2">
            <span class="text-sm">{{ item.label }}</span>
          </NuxtLink>
        </li>
      </ul>
    </div>

    <!-- Column 4: Logos + Copyright -->
    <div class="centered-flex">
      <div class="idf-icons" aria-label="Brand Logos">
        <div>
          <img
            v-once
            src="/images/icons/idmanify-logo-white.svg"
            class="idf-icon"
            alt="Idmanify"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            width="182"
            height="48"
          />
          <img
            v-once
            src="/images/logo/interactive-white.svg"
            class="idf-icon"
            alt="Idmanify Vision"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            width="182"
            height="48"
          />
        </div>

        <div>
          <img
            v-once
            src="/images/logo/blog-white.svg"
            class="idf-icon"
            alt="Idmanify Blog"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            width="182"
            height="48"
          />
          <img
            v-once
            src="/images/logo/kitchen-white.svg"
            class="idf-icon"
            alt="Idmanify Kitchen"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            width="182"
            height="48"
          />
        </div>
      </div>

      <div class="copyright xl:mt-12">
        <span class="mt-4 text-center">
          Copyright &copy; {{ currentYear }} idmanify
        </span>
        <span class="text-center mt-2">All Rights Reserved</span>

        <div class="mt-3">
          <s-icon icon="mail" dimension="24" classes="inline mr-1" />
          <span>info@idmanify.com</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const privacy = usePrivacyConsent();
/**
 * ✅ Single-file AppFooter.vue
 * Nuxt 4 + Tailwind 4 – SSR/Hydration/perf optimiert.
 *
 * Deine API liefert bereits:
 * - titles: Record<locale, string>
 * - slugs:  Record<locale, string>
 *
 * => Genau dadurch können wir beim Language Switch rein im Client umschalten,
 *    OHNE erneut /api/packages zu laden.
 */

type LocaleCode = string;

type PackageApiItem = {
  id: number;
  uuid: string;
  title?: string;
  slug?: string;
  titles?: Record<LocaleCode, string>;
  slugs?: Record<LocaleCode, string>;
  // Rest ist für Footer egal (picture, description, prices, etc.)
};

const { t, locale } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();

/**
 * Hydration-stabil: Jahr via useState (SSR & Client identisch).
 * Minimiert Edge-Case: Render genau um Mitternacht / Neujahr.
 */
const currentYear = useState<number>('footer-current-year', () =>
  new Date().getFullYear()
);

/**
 * ✅ SSR Fetch + Payload Cache
 * - Key ist konstant (nicht locale-abhängig) => KEIN Re-Fetch beim language switch
 * - server: true => SSR rendert die Liste direkt
 *
 * Wichtig:
 * - NICHT useLocalizedApiFetch verwenden, sonst hängt der Call oft an locale/headers
 *   und Nuxt behandelt es als "anderes Data-Set" beim locale switch.
 * - Stattdessen: ein Call, API liefert alle slugs/titles pro Sprache.
 */
const {
  data: packagesRaw,
  pending: packagesPending,
  error: packagesError
} = await useAsyncData<PackageApiItem[]>(
  'footer-packages-v1', // ✅ fix -> keine Reloads beim locale change
  async () => {
    return await $fetch<PackageApiItem[]>(
      `${config.public.API_URL}/api/packages`,
      {
        // baseURL: config.public?.apiBase ?? undefined
        // credentials: 'include', // falls nötig
      }
    );
  },
  {
    server: true,
    lazy: false,
    default: () => []
  }
);

/**
 * Helper: Locale-sichere Auswahl
 * - bevorzugt map[locale], fallback auf map['en'], fallback auf fallbackStr, sonst ""
 */
function pickLocalized(
  map: Record<string, string> | undefined,
  loc: string,
  fallbackStr?: string
) {
  return map?.[loc] ?? map?.en ?? fallbackStr ?? '';
}

/**
 * ✅ Computed: Package Links
 * - Wechselt bei locale switch sofort (nur computed)
 * - Keine Netzwerkanfrage, keine Slug-Neuberechnung über Server nötig
 */
const packageLinks = computed(() => {
  const loc = locale.value;

  // packagesRaw ist bereits cached aus SSR payload
  return (
    (packagesRaw.value ?? [])
      // Optional: Sortierung stabil nach id
      .slice()
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map((p) => {
        const label = pickLocalized(p.titles, loc, p.title);
        const slug = pickLocalized(p.slugs, loc, p.slug);

        /**
         * WICHTIG:
         * Wir geben bewusst einen "path" an -> `/program/${slug}`
         * und lassen nuxt-i18n daraus die locale Route bauen.
         *
         * => Wenn du pro Sprache unterschiedliche /program Slugs hast,
         *    ist das hier korrekt.
         */
        const to = localePath({ path: `/program/${slug}` });

        /**
         * Key muss stabil sein und darf NICHT label sein (ändert sich bei locale switch).
         * uuid ist perfekt, fallback id.
         */
        const key = p.uuid || String(p.id);

        return { key, label, to };
      })
  );
});

/**
 * ✅ Statische Site Links
 * - Nur Labels ändern sich mit i18n
 * - URLs sind named routes (localePath)
 */
const siteLinks = computed(() => {
  return [
    {
      key: 'sales',
      label: t('footer.idmanifyComItems.item3'),
      to: localePath({ name: 'sales' })
    },
    {
      key: 'return',
      label: t('footer.idmanifyComItems.item4'),
      to: localePath({ name: 'return' })
    },
    {
      key: 'privacy',
      label: t('footer.idmanifyComItems.item5'),
      to: localePath({ name: 'privacy' })
    },
    {
      key: 'legal',
      label: t('footer.idmanifyComItems.item6'),
      to: localePath({ name: 'legal' })
    },
    {
      key: 'terms',
      label: t('footer.idmanifyComItems.item2'),
      to: localePath({ name: 'terms' })
    },
    {
      key: 'faq',
      label: t('footer.idmanifyComItems.item1'),
      to: localePath({ name: 'faq' })
    },
    {
      key: 'about',
      label: t('footer.idmanifyComItems.item7'),
      to: localePath({ name: 'about' })
    },
    {
      key: 'contact',
      label: t('footer.idmanifyComItems.item8'),
      to: localePath({ name: 'contact' })
    }
  ];
});

/**
 * Optional: Debug/Monitoring (prod-safe: nur warnen)
 * - Wenn slug für locale fehlt, würdest du sonst /program/ (leer) erzeugen.
 */
watchEffect(() => {
  if (
    import.meta.dev &&
    !packagesPending.value &&
    (packagesRaw.value?.length ?? 0) > 0
  ) {
    const loc = locale.value;
    for (const p of packagesRaw.value ?? []) {
      const slug = pickLocalized(p.slugs, loc, p.slug);
      if (!slug) {
        console.warn(
          `[Footer] Missing slug for locale "${loc}" in package id=${p.id}`
        );
      }
    }
  }
});
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

/**
 * Tailwind 4 Styles – nahe an deinem Original, aber:
 * - keine line-height:0.2 (kann in manchen Fonts/Browsern "clipping" verursachen)
 * - Hydration/SSR stabil
 */

.idf-icons {
  @apply flex justify-center items-center space-x-4 my-6 mt-10;
}
.idf-icons > div {
  @apply icons-col space-y-4;
}

a {
  @apply hover:text-shade2;
}

.app-footer {
  @apply bg-shade1 pt-10 pb-4 px-8 text-white grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-10;
}
.centered-flex {
  @apply flex flex-col items-center;
}
.footer-heading {
  @apply font-bold text-lg text-gray-200;
}
.footer-heading-line {
  @apply relative w-10/12 h-0.5 rounded-full overflow-hidden;
}

.footer-heading-line::before {
  content: '';
  @apply absolute top-0 left-0 w-1/2 h-full bg-linear-to-l from-gray-600 to-black;
}
.footer-heading-line::after {
  content: '';
  @apply absolute top-0 right-0 w-1/2 h-full bg-linear-to-r from-gray-600 to-black;
}

@utility icons-col {
  @apply inline-flex flex-col justify-center items-center;
}

.idf-icon {
  @apply h-8 xl:h-12;
}

.payment-methods-icon {
  @apply w-3/5 sm:w-2/5 md:w-3/5 lg:w-full xl:w-full max-w-96;
}

.copyright {
  @apply flex flex-col items-center font-bold;
}

.idf-footer-sites {
  @apply flex flex-col items-center space-y-0.5 text-sm mt-2 text-center;
}

/* LI RESET */
.idf-footer-sites li::before {
  content: none;
}

.idf-footer-sites li {
  margin-bottom: 0;
  padding-top: 0.25rem;
  padding-left: 1rem;
  padding-right: 1rem;
  line-height: 1.1;
}

.idf-footer-sites li:first-child {
  padding-top: 0.15rem;
}
/* LI RESET END */
</style>
